/**
 * Accessibility utility functions
 */

/**
 * Generates a unique ID for form elements
 */
export const generateId = (prefix: string = "id"): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Gets accessible label for form fields
 */
export const getAccessibleLabel = (
  label?: string,
  required?: boolean
): string => {
  if (!label) return "";
  return required ? `${label} (required)` : label;
};

/**
 * Gets accessible error message
 */
export const getAccessibleErrorMessage = (error?: string): string => {
  return error || "";
};

/**
 * Manages focus trap for modals and dialogs
 */
export const trapFocus = (
  container: HTMLElement,
  firstFocusable?: HTMLElement,
  lastFocusable?: HTMLElement
) => {
  const focusableElements = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  const firstElement =
    firstFocusable || (focusableElements[0] as HTMLElement);
  const lastElement =
    lastFocusable ||
    (focusableElements[focusableElements.length - 1] as HTMLElement);

  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  };

  container.addEventListener("keydown", handleTabKey);

  return () => {
    container.removeEventListener("keydown", handleTabKey);
  };
};

/**
 * Moves focus to element
 */
export const moveFocus = (element: HTMLElement | null) => {
  if (element) {
    element.focus();
    if (element instanceof HTMLElement && element.setAttribute) {
      element.setAttribute("tabindex", "-1");
      element.focus();
    }
  }
};

/**
 * Checks if element is visible
 */
export const isElementVisible = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    style.opacity !== "0"
  );
};

/**
 * Gets all focusable elements within a container
 */
export const getFocusableElements = (
  container: HTMLElement
): HTMLElement[] => {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => isElementVisible(el)
  );
};

/**
 * Parses a hex color (#rgb or #rrggbb, with or without leading #) to [r,g,b] 0-255.
 * Returns null on anything it cannot parse, so callers can fail safe.
 */
const hexToRgb = (hex: string): [number, number, number] | null => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
};

/**
 * WCAG 2.1 relative luminance of a single sRGB channel (0-255 in).
 */
const channelLuminance = (value: number): number => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const relativeLuminance = ([r, g, b]: [number, number, number]): number =>
  0.2126 * channelLuminance(r) +
  0.7152 * channelLuminance(g) +
  0.0722 * channelLuminance(b);

/**
 * WCAG 2.1 contrast ratio between two hex colors (1:1 to 21:1).
 * Order-independent. Unparseable input yields 1 (worst case) rather than throwing,
 * so a bad value reads as "fails contrast" instead of crashing the UI.
 */
export const contrastRatio = (hexA: string, hexB: string): number => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 1;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * True when the foreground/background pair meets WCAG 2.1 AA:
 * 4.5:1 for normal text, 3:1 for large text (>=18.66px bold or >=24px).
 */
export const meetsContrastAA = (
  foreground: string,
  background: string,
  largeText: boolean = false
): boolean => contrastRatio(foreground, background) >= (largeText ? 3 : 4.5);

export type InvalidFieldTarget = string | HTMLElement | null | undefined | false;

export const isKeyboardSubmit = (): boolean => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return false;
  try {
    return active.matches(":focus-visible");
  } catch {
    return false;
  }
};

export const focusFirstInvalidField = (fields: InvalidFieldTarget[]) => {
  if (!isKeyboardSubmit()) return;
  setTimeout(() => {
    for (const field of fields) {
      if (!field) continue;
      const element =
        typeof field === "string" ? document.getElementById(field) : field;
      if (element) {
        element.focus();
        return;
      }
    }
  }, 0);
};

const rgbToHsl = ([r, g, b]: [number, number, number]): [
  number,
  number,
  number,
] => {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === rn
      ? (gn - bn) / d + (gn < bn ? 6 : 0)
      : max === gn
        ? (bn - rn) / d + 2
        : (rn - gn) / d + 4;
  return [h / 6, s, l];
};

const hslToHex = ([h, s, l]: [number, number, number]): string => {
  const hue = (p: number, q: number, t: number) => {
    const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let rgb: number[];
  if (s === 0) {
    rgb = [l, l, l];
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rgb = [hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)];
  }
  return (
    "#" +
    rgb
      .map((c) =>
        Math.round(c * 255)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
      .toUpperCase()
  );
};

/**
 * Darkens `hex` along its own hue until it reaches `minRatio` against
 * `background` (default 4.6:1 on white, just over the 4.5:1 AA text minimum).
 * A color that already passes comes back unchanged apart from upper-case
 * `#RRGGBB`; unparseable input comes back as is.
 *
 * For colors used both as a fill under white text and as text or an outline
 * on white, such as signer colors (WCAG 1.4.3 and 1.4.11).
 */
export const darkenToContrast = (
  hex: string,
  background: string = "#FFFFFF",
  minRatio: number = 4.6,
): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const [h, s, start] = rgbToHsl(rgb);
  let l = start;
  let out = hslToHex([h, s, l]);
  while (contrastRatio(out, background) < minRatio && l > 0) {
    l = Math.max(0, l - 0.005);
    out = hslToHex([h, s, l]);
  }
  return out;
};
